import { Avatar, Button, Card, Input, Modal, notification, Select, Table, Tag } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import CardTitle from '../../Card/CardTitle/CardTitle'
import { ClientEmail, ClientName, FilterContainer, FlexContainer, ModalContentWrapper, StyledPopover, StyledSelect, clientStyle } from './style'
import type { ColumnsType, TableProps } from 'antd/es/table';
import { useSelector } from 'react-redux'
import { ClientType } from '../../../types'
import ClientUserForm from '../../Forms/Client/User'
import { useCreateProfessionalClient, useDeleteProfessionalClient } from '../../../hooks/professionals/useProfessionalClient'
import { useUpdateClientActionPlanStatus } from '../../../hooks/professionals/useActionPlanStatuses'
import useGetProfessionalListClients from '../../../hooks/professionals/useGetProfessionalListClients';
import { theme } from '../../../styles/theme';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { NotificationErrorTitle, NotificationTitle } from '../../Common/Notification/style';
import CommonPopover from '../../Common/Popover';
import MaterialIcon from '../../Icons/MaterialIcon';
import { copyToClipboard } from '../../../helpers';

interface ActionPlanStatusType {
  id: string;
  color: string;
  name: string;
}

type FilterType = {
  name: string
  status: string[] | null
}

const ClientsTable = () => {
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();
  // const professionalListClients = useSelector((state: any) => state.professionals.listClients);
  const actionPlanStatuses = useSelector((state: any) => state.professionals.actionPlanStatuses);
  const actionPlanStatusesOptions = actionPlanStatuses?.map((actionPlanStatus: ActionPlanStatusType) => ({
    value: actionPlanStatus.id,
    label: actionPlanStatus.name
  }));
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filter, setFilter] = useState<FilterType>({
    name: '',
    status: null
  });
  const {
    data: professionalListClients,
    fetchData: fetchProfessionalListClients,
    total,
    page,
    loading,
    refetch
  } = useGetProfessionalListClients(currentPage, 10, filter);
  const { create: createProfessionalClient, loading: loadingCreateClient } = useCreateProfessionalClient();
  const { delete: deleteProfessionalClient, loading: loadingDeleteClient } = useDeleteProfessionalClient();
  const { update: updateClientActionPlanStatus } = useUpdateClientActionPlanStatus();
  const [openNewClient, setOpenNewClient] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<any>({
    open: false,
    id: null
  });
  const [openPopover, setOpenPopover] = useState<boolean>(false);
  const professionalListClientsWithKey = useMemo(() => (
    professionalListClients?.map((client: ClientType) => ({ ...client, key: client.id }))
  ), [professionalListClients])

  const columns: ColumnsType<ClientType> = [
    {
      title: '',
      dataIndex: 'notification',
    },
    {
      title: '',
      render: (client: ClientType) => (
        <Avatar
          style={{ color: 'white', fontSize: 20 }}
          size={{ xs: 50, sm: 50, md: 50, lg: 50, xl: 50, xxl: 50 }}
        >
          {`${client?.firstName.charAt(0)}${client?.familyName.charAt(0)}`}
        </Avatar>
      )
    },
    {
      title: 'Client',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.firstName.localeCompare(b.firstName),
      render: (client: ClientType) => (
        <>
          <ClientName>{`${client?.firstName} ${client?.familyName}`}</ClientName>
          <ClientEmail>{client?.email}</ClientEmail>
        </>
      )
    },
    {
      title: 'Validated',
      render: (client: ClientType) => (
        <>
          {client.accountStatus === 'active' ? (
            <Tag color="success">Active</Tag>
          ) : (
            <FlexContainer>
              <input value={client?.emailValidationUrl} type="text" style={{ display: 'none' }} />
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(client?.emailValidationUrl as string);
                }}
                icon={<MaterialIcon icon="content_copy" className="material-icons-sharp" />}
              />
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                }}
                icon={<MaterialIcon icon="send" className="material-icons-sharp" />}
              />
            </FlexContainer>
          )}
        </>
      )
    },
    {
      title: 'Status',
      responsive: ['lg'],
      render: (client: any) => (
        <>
          <StyledSelect
            value={client?.actionPlanStatus?.name ?? 'No Status'}
            options={actionPlanStatusesOptions}
            onChange={(_, option: any) => onActionPlanStatusUpdate(client.id, option.value)}
            style={{ width: 169 }}
            bgColor={client?.actionPlanStatus?.color}
            onClick={(e) => e.stopPropagation()}
          />
        </>
      )
    },
    {
      title: '',
      render: (client: ClientType) => (
        <StyledPopover
          trigger="click"
          placement="bottomRight"
          overlayInnerStyle={{ padding: 0 }}
          content={
            <CommonPopover
              onRemove={(e: React.MouseEvent) => {
                e.stopPropagation();
                setDeleteModal({
                  open: true,
                  id: client?.id
                })
                setOpenPopover(false);
              }}
            />
          }
          arrow={false}
          onOpenChange={(isOpen) => {
            if (isOpen) {
              return
            }
            setOpenPopover(false);
          }}
        >
          <Button onClick={(e: any) => {
            e.stopPropagation();
            setOpenPopover(true);
          }} type="text">
            <i className="material-icons-sharp">more_vert</i>
          </Button>
        </StyledPopover>
      )
    },
  ];

  const onChange: TableProps<ClientType>['onChange'] = (pagination, filters, sorter, extra) => {
    console.log('params', pagination, filters, sorter, extra);
  };

  const onDeleteClient = async (clientId: string) => {
    console.log('onDeleteClient', clientId);
    try {
      const res = await deleteProfessionalClient(clientId);
      console.log('onDeleteClient', res);
    } catch (error: any) {
      console.error(error);
    } finally {
      refetch();
      setDeleteModal({
        open: false,
        id: null
      });
    }
  };

  const handleBack = () => {
    setOpenNewClient(false);
    setDeleteModal({
      open: false,
      id: null
    })
  };

  const onCreateClient = async (values: any) => {
    try {
      const result = await createProfessionalClient(values);
      if (result && result.professionalCreateClient.success) {
        api.info({
          message: <NotificationTitle>{result.professionalCreateClient?.message}</NotificationTitle>,
          placement: 'topRight',
          icon:
            <CheckCircleOutlined
              style={{
                color: theme.color.primary,
              }}
            />,
          style: {
            background: `${theme.bg.secondary}`,
            borderRadius: 10,
            alignItems: 'center',
            display: 'flex'
          }
        });
        fetchProfessionalListClients();
        setOpenNewClient(false);
        return;
      }
      api.error({
        message: <NotificationErrorTitle>{result?.professionalCreateClient?.message}</NotificationErrorTitle>,
        placement: 'topRight',
        icon:
          <CloseCircleOutlined
            style={{
              color: theme.color.error,
            }}
          />,
        style: {
          background: `${theme.bg.error}`,
          borderRadius: 10,
          alignItems: 'center',
          display: 'flex'
        }
      });
    } catch (error: any) {
      api.error({
        message: <NotificationTitle>{error?.graphQLErrors[0].message}</NotificationTitle>,
        placement: 'topRight',
        icon:
          <CloseCircleOutlined
            style={{
              color: theme.color.error,
            }}
          />,
        style: {
          background: `${theme.bg.error}`,
          borderRadius: 10,
          alignItems: 'center',
          display: 'flex'
        }
      });
    }
  };

  const onActionPlanStatusUpdate = async (clientId: string, actionPlanStatusId: any) => {
    try {
      const res = await updateClientActionPlanStatus(clientId.toString(), actionPlanStatusId);
      console.log('onActionPlanStatusUpdate', res)
    } catch (error: any) {
      console.error('onActionPlanStatusUpdateError', error);
    } finally {
      refetch();
    }
  };

  const onVisitClient = (clientId: string) => {
    navigate(`clients/${clientId}`)
  };

  useEffect(() => {
    if (filter?.name || filter?.status) {
      fetchProfessionalListClients();
    }
  }, [filter, fetchProfessionalListClients]);

  console.log('first', professionalListClients)

  // if (loading) {
  //   return (
  //     <LoadingWrapper>
  //       <Spin size="large" />
  //     </LoadingWrapper>
  //   );
  // }

  return (
    <Card
      title={<CardTitle title='Clients' subTitle={`Showing ${total} items`} />}
      extra={<Button onClick={() => setOpenNewClient(true)} type="primary" icon={<i className="material-icons-sharp">add</i>} />}
      styles={{ header: { borderBottom: 0, alignItems: 'flex-start' } }}
      style={clientStyle}
    >
      {contextHolder}
      <FilterContainer>
        <Input
          value={filter?.name}
          size="large"
          placeholder="Name"
          prefix={<i className="material-icons-sharp">filter_list</i>}
          onChange={(e) => setFilter((prev: FilterType) => ({ ...prev, name: e.target.value }))}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ width: '100%' }}
          placeholder="Status"
          options={actionPlanStatusesOptions}
          onChange={(value) => setFilter((prev: FilterType) => ({ ...prev, status: value }))}
        />
      </FilterContainer>
      <Table
        loading={loading}
        size="small"
        columns={columns}
        dataSource={professionalListClientsWithKey}
        onChange={onChange}
        scroll={{ x: 'max-content' }}
        onRow={(record, rowIndex) => {
          return {
            onClick: (e: any) => {
              e.stopPropagation();
              onVisitClient(record.id);
            },
          };
        }}
        pagination={{
          total,
          current: page,
          onChange: (page, pageSize) => {
            setCurrentPage && setCurrentPage(page);
          }
        }}
      />
      <Modal
        title={`New Client`}
        open={openNewClient}
        onOk={onCreateClient}
        onCancel={handleBack}
        footer={null}
        width={900}
      >
        <ModalContentWrapper>
          <ClientUserForm
            onSubmit={onCreateClient}
            onBack={handleBack}
            loading={loadingCreateClient}
          />
        </ModalContentWrapper>
      </Modal>
      <Modal
        title={`Delete Client`}
        open={deleteModal.open}
        onOk={() => onDeleteClient(deleteModal?.id)}
        loading={loadingDeleteClient}
        onCancel={handleBack}
      // footer={null}
      >
        <ModalContentWrapper>
          <span>Are you sure you want to delete this client?</span>
        </ModalContentWrapper>
      </Modal>
    </Card>
  );
}

export default ClientsTable